<!-- eslint-disable quotes -->
<script setup lang="ts">
import FooterGrandNom from "~/components/layout/FooterGrandNom.vue";
const { trackSignupClick } = useAnalytics();
const { t } = useI18n();
const localePath = useLocalePath();

// Lien /register préservant l'attribution Google Ads (gclid/utm).
const registerLink = useRegisterLink();

// Libellés traduits (navigation.appFooter.*) ; les chemins vers une page de
// la liste blanche (i18n/pagesTraduites.ts) passent par localePath(), les
// pages FR-seules (recherche, produits, blog…) restent brutes — convention
// docs/architecture/i18n.md.
const columns = computed(() => [
  {
    // Maillage interne SEO : le footer est sur TOUTES les pages → chaque
    // listing (recherche) reçoit un lien depuis tout le site. C'est le
    // chemin de crawl Google vers les pages assos/cagnottes/événements
    // (les listings sont SSR et lient les fiches).
    label: t("navigation.appFooter.colonnes.decouvrir.titre"),
    children: [
      {
        label: t("navigation.appFooter.colonnes.decouvrir.associations"),
        to: "/recherche/associations",
      },
      {
        label: t("navigation.appFooter.colonnes.decouvrir.cagnottes"),
        to: "/recherche/cagnottes",
      },
      {
        label: t("navigation.appFooter.colonnes.decouvrir.evenements"),
        to: "/recherche/billetterie",
      },
      {
        label: t("navigation.appFooter.colonnes.decouvrir.adhesions"),
        to: "/recherche/adhesions",
      },
    ],
  },
  {
    label: t("navigation.appFooter.colonnes.produits.titre"),
    children: [
      {
        label: t("navigation.appFooter.colonnes.produits.liensDePaiement"),
        to: "/lien-de-paiement",
      },
      {
        label: t("navigation.appFooter.colonnes.produits.qrCodes"),
        to: "/qr-codes",
      },
      {
        label: t("navigation.appFooter.colonnes.produits.cagnotteEnLigne"),
        to: "/cagnotte",
      },
      {
        label: t("navigation.appFooter.colonnes.produits.boutiqueEnLigne"),
        to: "/boutique",
      },
      {
        label: t("navigation.appFooter.colonnes.produits.bornesDeDon"),
        to: "/bornes-de-don",
      },
      {
        label: t("navigation.appFooter.colonnes.produits.pageDeDon"),
        to: "/page-don",
      },
    ],
  },
  {
    label: t("navigation.appFooter.colonnes.outils.titre"),
    children: [
      {
        label: t("navigation.appFooter.colonnes.outils.dashboard"),
        to: "/dashboard",
      },
      {
        label: t("navigation.appFooter.colonnes.outils.recusFiscaux"),
        to: "/recus-fiscaux",
      },
      {
        label: t("navigation.appFooter.colonnes.outils.vueDEnsemble"),
        to: "/logiciel-association",
      },
    ],
  },
  {
    label: t("navigation.appFooter.colonnes.ressources.titre"),
    children: [
      {
        label: t("navigation.appFooter.colonnes.ressources.comparatifs"),
        to: "/comparateurs",
      },
      {
        label: t("navigation.appFooter.colonnes.ressources.formations"),
        to: "/formations",
      },
      {
        label: t("navigation.appFooter.colonnes.ressources.ideesDeCollecte"),
        to: "/projets",
      },
      {
        label: t("navigation.appFooter.colonnes.ressources.blog"),
        to: "/blog",
      },
      {
        label: t("navigation.appFooter.colonnes.ressources.centreAide"),
        to: localePath({ path: "/help" }),
      },
      {
        label: t("navigation.appFooter.colonnes.ressources.contact"),
        to: localePath({ path: "/contact" }),
      },
    ],
  },
  {
    label: t("navigation.appFooter.colonnes.legal.titre"),
    children: [
      {
        label: t("navigation.appFooter.colonnes.legal.mentionsLegales"),
        to: localePath({ path: "/mentions-legales" }),
      },
      {
        label: t("navigation.appFooter.colonnes.legal.cgu"),
        to: localePath({ path: "/terms" }),
      },
      {
        // Le programme de parrainage promet de l'argent : ses règles doivent
        // se trouver sans passer par le tableau de bord.
        label: t("navigation.appFooter.colonnes.legal.conditionsParrainage"),
        to: "/conditions-parrainage",
      },
      {
        label: t("navigation.appFooter.colonnes.legal.confidentialite"),
        to: localePath({ path: "/privacy" }),
      },
      {
        label: t("navigation.appFooter.colonnes.legal.charteEthique"),
        to: localePath({ path: "/charte-ethique" }),
      },
      {
        label: t("navigation.appFooter.colonnes.legal.conformite"),
        to: localePath({ path: "/conformite" }),
      },
    ],
  },
]);

const seoLinks = computed(() => [
  { label: t("navigation.appFooter.seoLiens.collecteDonsAssociation"), to: "/collecte-dons-association" },
  { label: t("navigation.appFooter.seoLiens.tpeAssociation"), to: "/tpe-association" },
  { label: t("navigation.appFooter.seoLiens.alternativeHelloasso"), to: "/alternative-helloasso" },
  { label: t("navigation.appFooter.seoLiens.alternativeAssoconnect"), to: "/alternative-assoconnect" },
  { label: t("navigation.appFooter.seoLiens.alternativeIraiser"), to: "/alternative-iraiser" },
  { label: t("navigation.appFooter.seoLiens.donRecurrent"), to: "/don-recurrent-association" },
  { label: t("navigation.appFooter.seoLiens.digitaliserAssociation"), to: "/digitaliser-association" },
  { label: t("navigation.appFooter.seoLiens.applicationMobile"), to: "/application-mobile-association" },
  { label: t("navigation.appFooter.seoLiens.carteMembre"), to: "/carte-membre-association" },
  { label: t("navigation.appFooter.seoLiens.collecteDonsEcole"), to: "/collecte-dons-ecole" },
  { label: t("navigation.appFooter.seoLiens.associationSportive"), to: "/collecte-dons-association-sportive" },
  { label: t("navigation.appFooter.seoLiens.associationHumanitaire"), to: "/collecte-dons-association-humanitaire" },
  { label: t("navigation.appFooter.seoLiens.associationCaritative"), to: "/collecte-dons-association-caritative" },
  { label: t("navigation.appFooter.seoLiens.associationCulturelle"), to: "/collecte-dons-association-culturelle" },
  { label: t("navigation.appFooter.seoLiens.associationHandicap"), to: "/collecte-dons-association-handicap" },
  { label: t("navigation.appFooter.seoLiens.associationEnfants"), to: "/collecte-dons-association-enfants" },
  { label: t("navigation.appFooter.seoLiens.associationAnimaux"), to: "/collecte-dons-association-animaux" },
  { label: t("navigation.appFooter.seoLiens.associationEnvironnement"), to: "/collecte-dons-association-environnement" },
  { label: t("navigation.appFooter.seoLiens.collecteDonsMosquee"), to: "/collecte-dons-mosquee" },
  { label: t("navigation.appFooter.seoLiens.collecteDonsRamadan"), to: "/collecte-dons-ramadan" },
  { label: t("navigation.appFooter.seoLiens.tombolaEnLigne"), to: "/projets/tombola" },
  { label: t("navigation.appFooter.seoLiens.kermesseEcole"), to: "/projets/kermesse" },
  { label: t("navigation.appFooter.seoLiens.lotoAssociatif"), to: "/projets/loto" },
  // Pages pays (analyse GEO 01/11/2026) : seul lien interne vers elles.
  { label: t("navigation.appFooter.seoLiens.belgique"), to: "/belgium" },
  { label: t("navigation.appFooter.seoLiens.luxembourg"), to: "/luxembourg" },
  { label: t("navigation.appFooter.seoLiens.paysBas"), to: "/netherlands" },
  { label: t("navigation.appFooter.seoLiens.allemagne"), to: "/germany" },
]);

// Tout en bas, Safari (iPhone) montre le fond de la page sous sa barre :
// noir comme ce pied de page, et non une bande blanche (Karim 2026-10-13).
const pied = useTemplateRef<HTMLElement>("pied");
useFondSousLePied(pied);
</script>

<template>
  <!-- Aucune marge haute ici (Karim 2026-08-08) : `mt-24` s'appliquait à
       TOUTES les pages alors qu'il n'avait été posé que pour décoller le
       bandeau « Paiement sécurisé » des pages adhésion du bord noir. Chaque
       page gère désormais son propre espacement — voir par exemple la marge
       basse des badges dans /a/[slug]. -->
  <!-- `relative z-10` : plusieurs pages publiques (boutique, /b, /a, fiches
       assos) ont une bannière en `position: fixed; z-index: 0` sous laquelle
       la page glisse. Un footer non positionné peint SOUS un élément fixe
       positionné : arrivé en bas de page, la bannière recouvrait le haut du
       footer (Karim 2026-10-07). Positionné + z-10, il passe par-dessus. -->
  <footer ref="pied" class="relative z-10 bg-black text-white">
    <!-- Top Section with CTA -->
    <div class="border-b border-slate-800">
      <UContainer>
        <div
          class="py-8 lg:py-10 flex flex-col lg:flex-row items-center justify-between gap-6"
        >
          <div class="text-center lg:text-left">
            <h3 class="text-2xl lg:text-3xl font-bold mb-2">
              {{ t("navigation.appFooter.pretAModerniserTitre") }}
            </h3>
            <p class="text-slate-400 text-base lg:text-lg">
              {{ t("navigation.appFooter.pretAModerniserTexte") }}
            </p>
          </div>
          <div class="flex flex-col sm:flex-row items-center gap-3">
            <UButton
              v-register-cta="'pied-de-page'"
              :to="registerLink"
              size="lg"
              color="neutral"
              class="rounded-full bg-white text-slate-900 hover:bg-slate-100 active:bg-gray-200 font-semibold px-6 border-2 border-white"
              @click="trackSignupClick('footer')"
            >
              {{ t("navigation.appFooter.inscrireAssociationGratuitement") }}
              <UIcon name="i-lucide-arrow-right" class="w-4 h-4 ml-1" />
            </UButton>
            <!-- Bouton Calendly (bordure animée bleue) : ouvre le modal de
                 prise de RDV global. Karim 2026-08-05. -->
            <CalendlyButton
              :label="t('navigation.appFooter.demoGratuite')"
              class="px-6 py-2.5 text-base font-semibold"
              icon-wrapper-class="w-6 h-6"
            />
          </div>
        </div>
      </UContainer>
    </div>

    <!-- Main Footer Content -->
    <UContainer>
      <div class="py-8 lg:py-10">
        <div
          class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 lg:gap-10"
        >
          <!-- Brand Column -->
          <div class="col-span-2 md:col-span-3 lg:col-span-1 mb-6 lg:mb-0">
            <NuxtLink to="/" class="flex items-center gap-2 mb-4">
              <!-- <img> natif + width/height explicites (audit SEO 2026-07-11) :
                   via NuxtImg le SVG passait par l'optimiseur Vercel en w=1536
                   pour 32px affichés, et l'absence de dimensions intrinsèques
                   était LA cause racine du CLS 0.906 mesuré par Lighthouse sur
                   /associations. Le CSS (h-8 w-auto) garde la main
                   sur la taille affichée : rendu identique. -->
              <img
                src="/newLogo/logo-mark-black.svg"
                alt="Donoor"
                width="256"
                height="256"
                class="h-8 w-auto rounded-md"
              />
              <span class="text-xl font-bold text-white">Donoor.</span>
            </NuxtLink>
            <p class="text-white text-sm mb-6 max-w-xs">
              {{ t("navigation.appFooter.tagline") }}
            </p>
            <!-- Social Links -->
            <div class="flex items-center gap-3">
              <a
                href="https://wa.me/33743715404"
                target="_blank"
                rel="noopener noreferrer nofollow"
                class="w-9 h-9 rounded-xl bg-white hover:bg-slate-200 flex items-center justify-center transition-colors"
                aria-label="WhatsApp"
              >
                <UIcon
                  name="i-simple-icons-whatsapp"
                  class="w-4 h-4 text-black"
                />
              </a>
              <a
                href="https://www.instagram.com/donoor_org"
                target="_blank"
                rel="noopener noreferrer"
                class="w-9 h-9 rounded-xl bg-white hover:bg-slate-200 flex items-center justify-center transition-colors"
                aria-label="Instagram"
              >
                <UIcon
                  name="i-simple-icons-instagram"
                  class="w-4 h-4 text-black"
                />
              </a>
              <a
                href="https://www.linkedin.com/company/donoor"
                target="_blank"
                rel="noopener noreferrer"
                class="w-9 h-9 rounded-xl bg-white hover:bg-slate-200 flex items-center justify-center transition-colors"
                aria-label="LinkedIn"
              >
                <UIcon
                  name="i-simple-icons-linkedin"
                  class="w-4 h-4 text-black"
                />
              </a>
              <a
                href="mailto:contact@donoor.org"
                class="w-9 h-9 rounded-xl bg-white hover:bg-slate-200 flex items-center justify-center transition-colors"
                aria-label="Email"
              >
                <UIcon name="i-lucide-mail" class="w-4 h-4 text-black" />
              </a>
            </div>
          </div>

          <!-- Links Columns -->
          <div
            v-for="column in columns"
            :key="column.label"
            class="flex flex-col"
          >
            <h4
              class="font-semibold text-white mb-4 text-sm uppercase tracking-wider"
            >
              {{ column.label }}
            </h4>
            <ul class="space-y-3">
              <li v-for="link in column.children" :key="link.label">
                <ULink
                  :to="link.to"
                  raw
                  class="text-sm transition-colors"
                  active-class="text-[#009DFF]! font-semibold"
                  inactive-class="text-white hover:text-slate-300"
                >
                  {{ link.label }}
                </ULink>
              </li>
            </ul>
          </div>
        </div>

        <!-- SEO Links Section -->
        <div class="border-t border-slate-800 mt-8 pt-8">
          <h4
            class="font-semibold text-white mb-4 text-sm uppercase tracking-wider"
          >
            {{ t("navigation.appFooter.voirPlus") }}
          </h4>
          <ul class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-6 gap-y-3">
            <li v-for="link in seoLinks" :key="link.to">
              <ULink
                :to="link.to"
                raw
                class="text-sm transition-colors"
                active-class="text-[#009DFF]! font-semibold"
                inactive-class="text-white hover:text-slate-300"
              >
                {{ link.label }}
              </ULink>
            </li>
          </ul>
        </div>
      </div>
    </UContainer>

    <!-- Bottom Bar -->
    <div class="border-t border-slate-800">
      <UContainer>
        <div
          class="py-6 flex flex-col sm:flex-row items-center justify-between gap-4"
        >
          <div class="flex flex-col sm:flex-row items-center gap-2 sm:gap-3">
            <p class="text-slate-500 text-sm text-center sm:text-left">
              © {{ new Date().getFullYear() }} Donoor. {{ t("navigation.appFooter.tousDroitsReserves") }}
            </p>
            <SelecteurLangue />
          </div>
          <div class="flex items-center gap-6">
            <NuxtLink
              :to="localePath({ path: '/terms' })"
              class="text-slate-500 hover:text-white text-sm transition-colors"
            >
              {{ t("navigation.appFooter.colonnes.legal.cgu") }}
            </NuxtLink>
            <NuxtLink
              :to="localePath({ path: '/privacy' })"
              class="text-slate-500 hover:text-white text-sm transition-colors"
            >
              {{ t("navigation.appFooter.colonnes.legal.confidentialite") }}
            </NuxtLink>
          </div>
        </div>
      </UContainer>
    </div>
    <!-- « Donoor » en très grand, gravé, le bas coupé par le bord (2026-10-13) -->
    <FooterGrandNom class="mt-2 overflow-hidden" />
  </footer>
</template>

<style scoped>
/* (Bouton WhatsApp remplacé par CalendlyButton — CSS de bordure animée
   déplacé dans le composant partagé. Karim 2026-08-05.) */
</style>
